import React, { useState, useEffect, useRef } from 'react';
import { MeasurementUnit, Point } from '@/types/shapes';
import GridLines from './GridLines';
import OriginIndicator from './OriginIndicator';
import { GridDragHandler } from './GridDragHandler';
import GridZoomControl from './GridZoomControl';
import { useGridZoom } from '@/contexts/GridZoomContext/index';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslate } from '@/utils/translate';

// Remove global flag as we'll handle initialization differently
// const HAS_COMPLETED_FIRST_RENDER = { value: false };

interface CanvasGridProps {
  canvasSize: { width: number, height: number };
  pixelsPerCm: number;
  pixelsPerMm: number;
  measurementUnit?: MeasurementUnit;
  onMoveAllShapes?: (dx: number, dy: number) => void;
  initialPosition?: Point | null;
  onPositionChange?: (position: Point) => void;
  showZoomControls?: boolean;
  isNonInteractive?: boolean;
  positionUpdateId?: number;
  forceCenterOnInit?: boolean;
  isEmbedded?: boolean;
  showUnits?: boolean;
  showPositiveQuadrant?: boolean;
}

const CanvasGrid: React.FC<CanvasGridProps> = ({
  canvasSize,
  pixelsPerCm,
  pixelsPerMm,
  measurementUnit = 'cm',
  onMoveAllShapes,
  initialPosition,
  onPositionChange,
  showZoomControls = true,
  isNonInteractive = false,
  positionUpdateId,
  forceCenterOnInit = false,
  isEmbedded = false,
  showUnits = true,
  showPositiveQuadrant = false
}) => {
  const { zoomFactor, setZoomFactor } = useGridZoom();
  const t = useTranslate();
  const isMobile = useIsMobile();
  const hasInitialized = useRef(false);
  const hasOriginMoved = useRef(false);
  const isHandlingExternalUpdate = useRef(false);
  const [origin, setOrigin] = useState<Point>({ x: 0, y: 0 });
  const getBottomLeft = () => {
    // Use padding whenever positive quadrant mode is enabled to align with visual padding
    const padding = showPositiveQuadrant ? 12 : 0;
    return { x: padding, y: canvasSize.height - padding };
  };
  const setOriginIfChanged = (p: Point) => {
    if (origin.x !== p.x || origin.y !== p.y) {
      setOrigin(p);
      return true;
    }
    return false;
  };
  const prevCanvasSizeRef = useRef({ width: 0, height: 0 });
  const lastAppliedUpdateIdRef = useRef<number | null>(null);
  // Disable grid dragging when non-interactive, embedded, or positive-quadrant locked
  const isDragDisabled = isNonInteractive || isEmbedded || showPositiveQuadrant;
  // Detect if URL already contains a grid param; if so, defer centering until initialPosition arrives
  const urlHasInitialGrid = typeof window !== 'undefined' && !forceCenterOnInit &&
    (() => {
      try {
        return new URL(window.location.href).searchParams.has('grid');
      } catch {
        return false;
      }
    })();

  // Root element ref for gesture bounds checks
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Pinch-to-zoom tracking refs
  const isPinchingRef = useRef(false);
  const initialPinchDistanceRef = useRef(0);
  const initialZoomRef = useRef(1);

  // Initialize the grid position once
  useEffect(() => {
    if (hasInitialized.current) return;
    
    if (showPositiveQuadrant && canvasSize.width > 0 && canvasSize.height > 0) {
      const bottomLeft = getBottomLeft();
      setOriginIfChanged(bottomLeft);
      // Suppress initial position change notification in positive-quadrant mode
    } else if (initialPosition) {
      setOriginIfChanged(initialPosition);
    } else if (forceCenterOnInit && canvasSize.width > 0 && canvasSize.height > 0) {
      const centerPoint = {
        x: Math.round(canvasSize.width / 2), 
        y: Math.round(canvasSize.height / 2) 
      };
      setOriginIfChanged(centerPoint);
    }
    
    hasInitialized.current = true;
  }, [initialPosition, canvasSize, showPositiveQuadrant, forceCenterOnInit]);

  // Update grid position when initialPosition changes
  useEffect(() => {
    if (!hasInitialized.current) return;
    // If URL already defines an initial grid and we haven't received initialPosition yet, avoid temporary centering
    if (urlHasInitialGrid && !initialPosition) {
      return;
    }

    // Force-apply externally requested initial position
    if (typeof positionUpdateId === 'number' && positionUpdateId !== lastAppliedUpdateIdRef.current) {
      lastAppliedUpdateIdRef.current = positionUpdateId;
      if (initialPosition) {
        isHandlingExternalUpdate.current = true;
        hasOriginMoved.current = false;
        setOrigin(initialPosition);
        setTimeout(() => {
          isHandlingExternalUpdate.current = false;
        }, 50);
      }
      return;
    }
    
    if (showPositiveQuadrant) {
      if (canvasSize.height > 0) {
        const bottomLeft = getBottomLeft();
        isHandlingExternalUpdate.current = true;
        const changed = setOriginIfChanged(bottomLeft);
        hasOriginMoved.current = false;
        if (changed && onPositionChange) onPositionChange(bottomLeft);
        setTimeout(() => { isHandlingExternalUpdate.current = false; }, 50);
      }
      return;
    }
    if (!initialPosition) {
      if (forceCenterOnInit && canvasSize.width > 0 && canvasSize.height > 0) {
        const centerPoint = {
          x: Math.round(canvasSize.width / 2), 
          y: Math.round(canvasSize.height / 2) 
        };
        
        isHandlingExternalUpdate.current = true;
        setOrigin(centerPoint);
        hasOriginMoved.current = false;
        
        setTimeout(() => {
          isHandlingExternalUpdate.current = false;
        }, 50);
      }
      return;
    }
    
    if (hasOriginMoved.current) return;
    
    isHandlingExternalUpdate.current = true;
    setOrigin(initialPosition);
    
    setTimeout(() => {
      isHandlingExternalUpdate.current = false;
    }, 50);
  }, [initialPosition, canvasSize, positionUpdateId]);

  // Handle canvas size changes (only center if opted-in; or bottom-left when positive quadrant)
  useEffect(() => {
    // If an explicit initialPosition is provided or URL has an initial grid, do not auto-center on resize
    if (showPositiveQuadrant) {
      const bottomLeft = getBottomLeft();
      setOriginIfChanged(bottomLeft);
      // Suppress parent updates on resize in positive-quadrant to avoid oscillation
      prevCanvasSizeRef.current = { ...canvasSize };
      return;
    }
    if (initialPosition || urlHasInitialGrid || !forceCenterOnInit) {
      return;
    }
    const prevSize = prevCanvasSizeRef.current;
    const currentSize = canvasSize;
    
    prevCanvasSizeRef.current = { ...currentSize };
    
    const hasSignificantChange = 
      (Math.abs(prevSize.width - currentSize.width) > 50 || 
       Math.abs(prevSize.height - currentSize.height) > 50) &&
      currentSize.width > 0 && currentSize.height > 0;
    
    const isFirstMeaningfulSize = 
      (prevSize.width === 0 || prevSize.height === 0) && 
      currentSize.width > 0 && currentSize.height > 0;
    
    if (hasSignificantChange || isFirstMeaningfulSize) {
      if (!hasOriginMoved.current) {
        const newOrigin = {
          x: Math.floor(currentSize.width / 2),
          y: Math.floor(currentSize.height / 2)
        };
        
        setOrigin(newOrigin);
        if (onPositionChange) onPositionChange(newOrigin);
      }
    }
  }, [canvasSize, initialPosition, onPositionChange]);

  // Enable pinch-to-zoom on mobile (works with or without visible controls)
  useEffect(() => {
    if (!isMobile) return; // only on mobile devices
    const rootEl = rootRef.current;
    if (!rootEl) return;

    const getDistance = (touches: TouchList) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    const areTouchesInside = (touches: TouchList) => {
      const rect = rootEl.getBoundingClientRect();
      for (let i = 0; i < Math.min(2, touches.length); i++) {
        const t = touches[i];
        if (t.clientX < rect.left || t.clientX > rect.right || t.clientY < rect.top || t.clientY > rect.bottom) {
          return false;
        }
      }
      return true;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2 && areTouchesInside(e.touches)) {
        isPinchingRef.current = true;
        initialPinchDistanceRef.current = getDistance(e.touches);
        initialZoomRef.current = zoomFactor;
        e.preventDefault();
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isPinchingRef.current) return;
      if (e.touches.length !== 2) return;
      if (!areTouchesInside(e.touches)) return;
      const distance = getDistance(e.touches);
      const scale = distance / Math.max(1, initialPinchDistanceRef.current);
      // Reuse the same path as buttons: update via setZoomFactor (clamped in provider)
      const newZoom = initialZoomRef.current * scale;
      setZoomFactor(newZoom);
      e.preventDefault();
    };

    const endPinch = () => {
      isPinchingRef.current = false;
    };

    window.addEventListener('touchstart', onTouchStart, { passive: false });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', endPinch, { passive: false });
    window.addEventListener('touchcancel', endPinch, { passive: false });

    return () => {
      window.removeEventListener('touchstart', onTouchStart as any);
      window.removeEventListener('touchmove', onTouchMove as any);
      window.removeEventListener('touchend', endPinch as any);
      window.removeEventListener('touchcancel', endPinch as any);
    };
  }, [isMobile, setZoomFactor, zoomFactor]);

  // Enable trackpad pinch-zoom on desktop (ctrl+wheel in Chromium/Firefox, gesture events in Safari)
  useEffect(() => {
    const rootEl = rootRef.current;
    if (!rootEl) return;

    const isInside = (clientX: number, clientY: number) => {
      const rect = rootEl.getBoundingClientRect();
      return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
    };

    // Chromium/Firefox: ctrlKey + wheel indicates pinch on trackpads
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return; // treat only pinch-zoom
      if (!isInside(e.clientX, e.clientY)) return;
      // Convert wheel delta to scale factor (tune sensitivity)
      const scale = Math.exp(-e.deltaY * 0.002);
      const newZoom = zoomFactor * scale;
      setZoomFactor(newZoom);
      e.preventDefault();
    };

    // Safari: gesture events
    const onGestureStart = (e: any) => {
      if (!isInside(e.clientX ?? 0, e.clientY ?? 0)) return;
      // prevent browser zoom
      e.preventDefault?.();
    };
    const onGestureChange = (e: any) => {
      if (!isInside(e.clientX ?? 0, e.clientY ?? 0)) return;
      // e.scale is relative; multiply current zoom by scale delta
      const scale = e.scale || 1;
      const newZoom = zoomFactor * scale;
      setZoomFactor(newZoom);
      e.preventDefault?.();
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    // Safari gesture events (non-standard)
    window.addEventListener('gesturestart', onGestureStart as EventListener, { passive: false } as AddEventListenerOptions);
    window.addEventListener('gesturechange', onGestureChange as EventListener, { passive: false } as AddEventListenerOptions);

    return () => {
      window.removeEventListener('wheel', onWheel as EventListener);
      window.removeEventListener('gesturestart', onGestureStart as EventListener);
      window.removeEventListener('gesturechange', onGestureChange as EventListener);
    };
  }, [setZoomFactor, zoomFactor]);

  return (
    <div 
      ref={rootRef}
      className="canvas-grid" 
      style={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '100%', 
        pointerEvents: 'none',
        zIndex: 5
      }}
    >
      <GridDragHandler
        origin={origin}
        onOriginChange={isDragDisabled ? undefined : (newOrigin) => {
          if (isHandlingExternalUpdate.current) return;
          
          hasOriginMoved.current = true;
          setOrigin(newOrigin);
          
          if (onPositionChange) {
            onPositionChange(newOrigin);
          }
        }}
        onMoveAllShapes={isDragDisabled ? undefined : onMoveAllShapes}
        pixelsPerSmallUnit={pixelsPerMm}
        isNonInteractive={isDragDisabled}
      >
        <GridLines
          canvasSize={canvasSize}
          pixelsPerCm={pixelsPerCm}
          pixelsPerMm={pixelsPerMm}
          measurementUnit={measurementUnit}
          origin={showPositiveQuadrant ? { x: 0, y: canvasSize.height } : origin}
          isEmbedded={isEmbedded}
          showUnits={showUnits}
          showPositiveQuadrant={showPositiveQuadrant}
        />
        <OriginIndicator
          origin={showPositiveQuadrant ? getBottomLeft() : origin}
          canvasSize={canvasSize}
        />
      </GridDragHandler>
      {showZoomControls && (
        <div style={{ position: 'relative', zIndex: 50 }}>
          <GridZoomControl />
        </div>
      )}
      {/* Translated legend (hide on mobile to avoid showing keyboard shortcuts) */}
      {!isDragDisabled && !isMobile && (
        <div
          className="pointer-events-none select-none"
          style={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: 'white',
            padding: '5px 10px',
            borderRadius: 4,
            fontSize: 12,
            opacity: 0.7
          }}
        >
          {t('gridHint.moveOrigin')} | {t('gridHint.moveAll')}
        </div>
      )}
    </div>
  );
};

export default CanvasGrid; 